import React, { useMemo } from 'react';
import { processarMedicoes } from '../utils/calculosSonoros';

/**
 * Componente de Resultados e Análise
 * Exibe a lista de pontos e os resultados calculados conforme NBR 10151
 */
const Resultados = ({ pontos, onRemovePonto, onClearAll }) => {
  // Processa medições e calcula resultados
  const resultados = useMemo(() => {
    if (pontos.length < 2) return null;
    return processarMedicoes(pontos);
  }, [pontos]);

  // Separa pontos por tipo
  const pontosResiduais = pontos.filter(p => p.tipo === 'residual');
  const pontosEspecificos = pontos.filter(p => p.tipo === 'especifico');

  if (pontos.length === 0) {
    return (
      <div className="resultados-container">
        <div className="resultados-empty">
          <h3>📋 Pontos de Medição</h3>
          <p>Nenhum ponto adicionado ainda.</p>
          <p className="hint">Realize medições e adicione pontos usando o formulário acima.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="resultados-container">
      {/* Lista de pontos adicionados */}
      <div className="pontos-lista">
        <h3>📍 Pontos Adicionados ({pontos.length})</h3>
        
        {/* Pontos Residuais */}
        {pontosResiduais.length > 0 && (
          <div className="pontos-grupo">
            <h4>🔵 Residuais (Referência)</h4>
            {pontosResiduais.map((ponto, index) => (
              <div key={ponto.timestamp || index} className="ponto-card residual">
                <div className="ponto-info">
                  <span className="ponto-legenda">{ponto.legenda}</span>
                  <span className="ponto-stats">
                    LAeq: <strong>{ponto.stats?.laeq?.toFixed(1) || 'N/A'} dB</strong>
                    {' | '}
                    Max: {ponto.stats?.max?.toFixed(1) || 'N/A'} dB
                    {' | '}
                    {ponto.stats?.count || 0} amostras
                  </span>
                  <span className="ponto-meta">
                    Ref: {ponto.valorReferencia} dB | {ponto.legislacao}
                  </span>
                </div>
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={() => onRemovePonto(pontos.indexOf(ponto))}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pontos Específicos */}
        {pontosEspecificos.length > 0 && (
          <div className="pontos-grupo">
            <h4>🔴 Específicos (Com Fonte Investigada)</h4>
            {pontosEspecificos.map((ponto, index) => (
              <div key={ponto.timestamp || index} className="ponto-card especifico">
                <div className="ponto-info">
                  <span className="ponto-legenda">{ponto.legenda}</span>
                  <span className="ponto-stats">
                    LAeq: <strong>{ponto.stats?.laeq?.toFixed(1) || 'N/A'} dB</strong>
                    {' | '}
                    Max: {ponto.stats?.max?.toFixed(1) || 'N/A'} dB
                    {' | '}
                    {ponto.stats?.count || 0} amostras
                  </span>
                  <span className="ponto-meta">
                    Ref: {ponto.valorReferencia} dB | {ponto.legislacao}
                  </span>
                </div>
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={() => onRemovePonto(pontos.indexOf(ponto))}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="pontos-actions">
          <button 
            className="btn btn-outline-danger"
            onClick={onClearAll}
          >
            🗑️ Limpar Todos os Pontos
          </button>
        </div>
      </div>

      {/* Mensagem se faltar pontos */}
      {(pontosResiduais.length === 0 || pontosEspecificos.length === 0) && (
        <div className="resultados-aviso">
          <h4>⚠️ Pontos Necessários</h4>
          <p>
            Para calcular os resultados, adicione:
            {pontosResiduais.length === 0 && <><br/>• Pelo menos 1 ponto <strong>Residual</strong> (sem a fonte sonora)</>}
            {pontosEspecificos.length === 0 && <><br/>• Pelo menos 1 ponto <strong>Específico</strong> (com a fonte sonora)</>}
          </p>
        </div>
      )}

      {/* Resultados calculados */}
      {resultados && !resultados.erro && (
        <div className="resultados-analise">
          <h3>📊 Análise Conforme NBR 10151</h3>

          {/* Resumo do Residual */}
          <div className="resultado-residual">
            <h4>Ponto de Referência (Residual)</h4>
            <div className="resultado-grid">
              <div className="resultado-item">
                <span className="label">LAeq Residual:</span>
                <span className="valor">{resultados.residual.laeq.toFixed(1)} dB</span>
              </div>
              <div className="resultado-item">
                <span className="label">Máximo:</span>
                <span className="valor">{resultados.residual.maximo.toFixed(1)} dB</span>
              </div>
              <div className="resultado-item">
                <span className="label">Mínimo:</span>
                <span className="valor">{resultados.residual.minimo.toFixed(1)} dB</span>
              </div>
            </div>
          </div>

          {/* Tabela de Resultados */}
          <div className="resultados-tabela">
            <h4>Resultados por Ponto</h4>
            <div className="tabela-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Ponto</th>
                    <th>Método</th>
                    <th>LAeq (Ltot)</th>
                    <th>Lres</th>
                    <th>Lesp</th>
                    <th>Lresp</th>
                    <th>Referência</th>
                    <th>Atende?</th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.especificos.map((r, index) => (
                    <tr key={index} className={r.atende ? 'atende' : 'nao-atende'}>
                      <td>{r.legenda}</td>
                      <td>
                        <span className={`badge ${r.metodo === 'Detalhado' ? 'warning' : 'info'}`}>
                          {r.metodo}
                        </span>
                      </td>
                      <td>{r.laeq.toFixed(1)}</td>
                      <td>{r.lresidual.toFixed(1)}</td>
                      <td>
                        {r.lesp.valor !== null 
                          ? r.lesp.valor.toFixed(1) 
                          : <span className="text-muted">N/A</span>}
                      </td>
                      <td>
                        {r.lresp !== null 
                          ? r.lresp.toFixed(1) 
                          : <span className="text-muted">N/A</span>}
                      </td>
                      <td>{r.valorReferencia}</td>
                      <td>
                        <span className={`badge ${r.atende ? 'success' : 'danger'}`}>
                          {r.atende ? '✓ Sim' : '✕ Não'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detalhamento por ponto */}
          <div className="resultados-detalhes">
            <h4>Detalhamento</h4>
            {resultados.especificos.map((r, index) => (
              <div key={index} className={`detalhe-card ${r.atende ? 'atende' : 'nao-atende'}`}>
                <h5>{r.legenda}</h5>
                <div className="detalhe-grid">
                  <div className="detalhe-item">
                    <span className="label">Coordenadas:</span>
                    <span className="valor">X: {r.coordenadaX || 'N/I'}, Y: {r.coordenadaY || 'N/I'}</span>
                  </div>
                  <div className="detalhe-item">
                    <span className="label">Legislação:</span>
                    <span className="valor">{r.legislacao}</span>
                  </div>
                  <div className="detalhe-item">
                    <span className="label">Equipamento:</span>
                    <span className="valor">{r.equipamento}</span>
                  </div>
                  <div className="detalhe-item">
                    <span className="label">Som Tonal:</span>
                    <span className={`valor ${r.tonal.presente ? 'warning' : ''}`}>
                      {r.tonal.presente 
                        ? `Sim (${r.tonal.frequencias.join(', ')} Hz)` 
                        : 'Não detectado'}
                    </span>
                  </div>
                  <div className="detalhe-item">
                    <span className="label">Som Impulsivo:</span>
                    <span className={`valor ${r.impulsivo ? 'warning' : ''}`}>
                      {r.impulsivo ? 'Sim' : 'Não detectado'}
                    </span>
                  </div>
                  <div className="detalhe-item">
                    <span className="label">Observação Lesp:</span>
                    <span className="valor">{r.lesp.observacao}</span>
                  </div>
                </div>
                <div className={`conclusao ${r.atende ? 'positiva' : 'negativa'}`}>
                  {r.atende 
                    ? `✓ O nível de resposta (${r.lresp?.toFixed(1) || 'N/A'} dB) está DENTRO do limite de ${r.valorReferencia} dB estabelecido.`
                    : `✕ O nível de resposta (${r.lresp?.toFixed(1) || 'N/A'} dB) ULTRAPASSA o limite de ${r.valorReferencia} dB estabelecido.`}
                </div>
              </div>
            ))}
          </div>

          {/* Legenda de métodos */}
          <div className="resultados-legenda">
            <h4>📖 Legenda</h4>
            <ul>
              <li><strong>LAeq (Ltot):</strong> Nível de pressão sonora equivalente contínuo total medido</li>
              <li><strong>Lres:</strong> Nível residual (sem a fonte sonora investigada)</li>
              <li><strong>Lesp:</strong> Nível específico da fonte sonora investigada</li>
              <li><strong>Lresp:</strong> Nível de resposta (Lesp + correções por som tonal/impulsivo)</li>
              <li><strong>Método Simplificado:</strong> Sem presença de som tonal ou impulsivo</li>
              <li><strong>Método Detalhado:</strong> Com correção de +5 dB para som tonal e/ou impulsivo</li>
            </ul>
          </div>
        </div>
      )}

      {resultados?.erro && (
        <div className="resultados-erro">
          ⚠️ {resultados.erro}
        </div>
      )}
    </div>
  );
};

export default Resultados;
